/**
 * jump_arc.ts — the shape of a jump (physics follow-up A, 2026-10-05; `audit-feel.md` G2). PURE.
 *
 * The walker jumped at 6.1 m/s and fell under the same 9.81 it rose under (`PhysicsModule`): a symmetric, floaty
 * parabola with no way to make a small hop. Platformers have settled what reads well (the engine's own
 * `CharMovementSystem` ships jumpCut ×3 / fall ×2; Celeste, Hollow Knight, Mario all use some form of it):
 *
 *   · a FALL MULTIPLIER — descent under JUMP_FALL_MULT × g: the arc comes down heavier than it went up, so a jump
 *     lands decisively instead of drifting (every fall, a ledge's too — a body does not float off an edge);
 *   · an APEX HANG — inside ±JUMP_APEX_BAND of the top, while the jump is HELD, gravity drops to JUMP_APEX_GRAVITY:
 *     a short hang where a player aims a landing (the reward for holding);
 *   · a JUMP CUT — releasing jump while still rising scales the upward velocity by JUMP_CUT_SCALE once: a tap is a
 *     hop, a hold is the full 1.9 m. Armed only when the button was held at launch (a buffered press already released
 *     when the jump fires gets the full jump — the buffer's intent), and never for a jump PAD (it is not your legs).
 *
 * Height budget, measured by the harness: a held jump peaks ≈ 1.93 m (was 1.90 — the hang adds a few cm, the mantle and
 * autostep thresholds are untouched), lands in ≈ 1.27 s (was 1.24), and lands at ≈ 7.9 m/s (was 6.1); a tap released
 * on the next tick peaks ≈ 0.6 m. WALKER HEIGHT IS CLIENT-ONLY: the server integrates the 2D intent (`moveSteps2`) and
 * reads the jump flag only for the animState string; the reconcile re-bases XZ with keepY — nothing server-side changes.
 * Special gravity (an Edit-Mode gravity zone, the personal world's water preset) keeps the plain integrator.
 */

/** Descent gravity multiplier (the engine's CharMovementSystem uses ×2; ×1.7 keeps the 1.2 s air time a mantle and the
 *  jump buffer were tuned around). */
export const JUMP_FALL_MULT = 1.7;
/** |vy| under this (m/s) is "the top of the arc". */
export const JUMP_APEX_BAND_MPS = 1.0;
/** Gravity multiplier inside the apex band while the jump is held. */
export const JUMP_APEX_GRAVITY = 0.55;
/** Releasing jump while rising keeps this share of the upward velocity (once). */
export const JUMP_CUT_SCALE = 0.55;

export interface JumpArc {
  /** The current airborne spell began with a jump of the legs (not a pad, not a ledge). */
  fromJump: boolean;
  /** The button was held at launch and has not been released yet — a release now cuts. */
  cutArmed: boolean;
  /** The cut already happened this spell. */
  cut: boolean;
  /** Diagnostics. */
  cuts: number; hangTicks: number;
}
export function newJumpArc(): JumpArc { return { fromJump: false, cutArmed: false, cut: false, cuts: 0, hangTicks: 0 }; }
/** A jump of the legs launched; `held` = the button is down at the launch tick. */
export function jumpLaunched(a: JumpArc, held: boolean): void { a.fromJump = true; a.cutArmed = held; a.cut = false; }
/** A launch that is not the legs (a jump pad, a mantle hand-off, a teleport): no cut, no hang. */
export function externalLaunch(a: JumpArc): void { a.fromJump = false; a.cutArmed = false; a.cut = false; }
/** Feet down: the spell is over. */
export function arcLanded(a: JumpArc): void { a.fromJump = false; a.cutArmed = false; a.cut = false; }

/** PURE: the gravity multiplier for this tick (after any cut). */
export function arcGravityScale(a: JumpArc, vy: number, held: boolean): number {
  if (Math.abs(vy) <= JUMP_APEX_BAND_MPS && a.fromJump && held && !a.cut) return JUMP_APEX_GRAVITY;
  if (vy < 0) return JUMP_FALL_MULT;
  return 1;
}

/**
 * One airborne fixed tick: apply the jump cut (on the release edge), then integrate gravity `g` (m/s², already ×
 * any gravity scale) with the arc's multiplier. `shaped` false = the plain integrator (special gravity). Returns vy.
 */
export function jumpArcStep(a: JumpArc, vy: number, held: boolean, g: number, dt: number, shaped = true): number {
  if (!shaped) return vy - g * dt;
  let v = vy;
  if (a.fromJump && a.cutArmed && !held && v > 0 && !a.cut) { v *= JUMP_CUT_SCALE; a.cut = true; a.cutArmed = false; a.cuts++; }
  if (held === false && !a.cut) a.cutArmed = false; // released on the way down: nothing left to cut
  const k = arcGravityScale(a, v, held);
  if (k === JUMP_APEX_GRAVITY) a.hangTicks++;
  return v - g * k * dt;
}

/** The camera's landing dip (rad, ≤ 0) for a touchdown speed (m/s): nothing under 3 m/s, ~0.7° for a jump, capped. */
export const LAND_DIP_MIN_MPS = 3;
export const LAND_DIP_PER_MPS = 0.0025;
export const LAND_DIP_MAX_RAD = 0.03;
export function landingDip(impactMps: number): number {
  if (!(impactMps > LAND_DIP_MIN_MPS)) return 0;
  return -Math.min(LAND_DIP_MAX_RAD, (impactMps - LAND_DIP_MIN_MPS) * LAND_DIP_PER_MPS);
}
